import { createContext, useContext, useEffect, useMemo } from 'react';
import { ClerkProvider, useAuth } from '@clerk/clerk-react';
import { SIGN_IN_POOL, type SignInPool } from '@bcecloud/portal-ui';
import { getAuthPool, publishableKeyFor } from '@/lib/auth-pool';
import { setClerkSignOut, setClerkTokenProvider } from '@/lib/clerk-token';

export interface MyEventsAuthProviderProps {
  readonly children: React.ReactNode;
}

/** Whether a ClerkProvider is actually mounted, and for which pool. */
export interface ClerkPoolState {
  /**
   * True only when a `ClerkProvider` really is above this point in the tree.
   * Anything calling a Clerk hook MUST gate on this rather than re-deriving it
   * from storage: the two answers can disagree — storage is mutable and the
   * provider fixes its key at mount — and a component that renders a Clerk
   * hook without a provider crashes the whole route.
   */
  readonly mounted: boolean;
  /** The pool that is mounted. Meaningless when `mounted` is false. */
  readonly pool: SignInPool;
}

const ClerkPoolContext = createContext<ClerkPoolState>({
  mounted: false,
  pool: SIGN_IN_POOL.client,
});

/**
 * Whether Clerk is mounted here, and for which pool.
 *
 * @returns The live provider state.
 */
export function useClerkPool(): ClerkPoolState {
  return useContext(ClerkPoolContext);
}

/** Where BCE staff sign in — the team instance's primary domain. */
const STAFF_SIGN_IN_URL = 'https://app.bcecloud.com/';

/** The team instance's primary domain; any other production host is a satellite. */
const STAFF_PRIMARY_DOMAIN = 'bcecloud.com';

/**
 * Whether the staff pool must mount as a Clerk satellite on this host.
 *
 * @param hostname - `window.location.hostname`.
 * @returns True on a production host outside the team instance's domain;
 *   false under bcecloud.com and in local development, where the dev
 *   instance's key is valid anywhere.
 */
export function isSatelliteHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  if (host === 'localhost' || host.endsWith('.localhost') || host.endsWith('.localtest.me')) return false;
  return host !== STAFF_PRIMARY_DOMAIN && !host.endsWith(`.${STAFF_PRIMARY_DOMAIN}`);
}

/**
 * Mounts Clerk for myevents against whichever pool this session belongs to.
 *
 * Exactly one instance is live per document — Clerk's singleton cannot hold
 * two — so the pool is read once here at mount, and switching it reloads the
 * page (see `switchAuthPool`). The default is the customer instance; a BCE
 * address routes into the team instance instead.
 *
 * When neither key is configured the app still renders. myevents' password
 * sign-in remains wired up underneath, so a missing or misconfigured Clerk
 * instance degrades to the way customers signed in before rather than locking
 * the portal.
 *
 * @param props - The app to render inside the provider.
 * @returns The provider tree.
 */
export function MyEventsAuthProvider({
  children,
}: MyEventsAuthProviderProps): React.ReactElement {
  const pool = getAuthPool();
  const publishableKey = publishableKeyFor(pool);
  // Read once per mount and shared downward, so no consumer can reach a
  // different conclusion than the one this component actually acted on.
  const state = useMemo<ClerkPoolState>(
    () => ({ mounted: publishableKey.length > 0, pool }),
    [publishableKey, pool],
  );

  if (!publishableKey) {
    return <ClerkPoolContext.Provider value={state}>{children}</ClerkPoolContext.Provider>;
  }

  const inner = (
    <ClerkPoolContext.Provider value={state}>
      <ClerkTokenBridge pool={pool} />
      {children}
    </ClerkPoolContext.Provider>
  );

  // Staff arrive already signed in to BCEcloud. When this app is served under
  // bcecloud.com the team instance's cookie is simply valid here. On its own
  // domain (app.mybcecloud.com) the host is a registered satellite of the team
  // instance instead: ClerkJS syncs the session from the primary and, with no
  // session there, sends the person to BCEcloud to sign in (a satellite cannot
  // host sign-in itself). Customers never take this path — their instance's
  // primary domain is this apex.
  if (pool === SIGN_IN_POOL.staff && isSatelliteHost(window.location.hostname)) {
    return (
      <ClerkProvider
        publishableKey={publishableKey}
        isSatellite
        domain={window.location.hostname}
        signInUrl={STAFF_SIGN_IN_URL}
        afterSignOutUrl="/auth/login"
      >
        {inner}
      </ClerkProvider>
    );
  }

  return (
    <ClerkProvider publishableKey={publishableKey} afterSignOutUrl="/auth/login">
      {inner}
    </ClerkProvider>
  );
}

/**
 * Registers Clerk's token getter so API calls carry the Clerk session.
 *
 * Rendered inside the provider because `useAuth` requires it. Nothing is
 * rendered — this exists purely so the axios interceptor has a way to ask for
 * a fresh token without importing Clerk itself.
 *
 * @param props - Which pool is mounted, so callers can tell staff from client.
 * @returns Nothing.
 */
function ClerkTokenBridge({ pool }: { readonly pool: string }): null {
  const { getToken, isSignedIn, signOut } = useAuth();

  useEffect(() => {
    setClerkTokenProvider({
      getToken: async () => (isSignedIn ? await getToken() : null),
      isStaffSession: pool === SIGN_IN_POOL.staff,
    });
    setClerkSignOut(async () => {
      await signOut();
    });
  }, [getToken, isSignedIn, pool, signOut]);

  return null;
}
